import React from 'react';
import { useParams } from 'react-router-dom';

import { ChildStep, ParentStep } from '@/types/steps';

type WithParent<Q, T> = Q & { parent: ParentStep<T> };

export function useStepperStep<T>(
  steps: readonly ParentStep<T>[],
): WithParent<ChildStep<T[keyof T] | T>, T> {
  const { parentId, childId } = useParams();

  const emptyParentStep = React.useMemo(
    (): ParentStep<T> => ({
      path: '',
      type: '',
      title: '',
      children: [],
    }),
    [],
  );

  const parent = React.useMemo(
    (): ParentStep<T> => steps.find(idMatcher(parentId)) ?? emptyParentStep,
    [emptyParentStep, parentId, steps],
  );

  const { children } = parent;

  const emptyChild = React.useMemo(
    (): ChildStep<T[keyof T] | T> => ({
      path: '',
      accessor: [],
      type: '',
      title: '',
      fields: [],
    }),
    [],
  );

  const child = React.useMemo(
    (): ChildStep<T[keyof T] | T> => children.find(idMatcher(childId)) ?? emptyChild,
    [childId, children, emptyChild],
  );

  return { ...child, parent: parent };
}

const idMatcher =
  <T>(
    wantedId: string | undefined,
  ): ((step: ParentStep<T> | ChildStep<T[keyof T] | T>) => boolean) =>
  (step: ParentStep<T> | ChildStep<T[keyof T] | T>): boolean =>
    step.path === wantedId;
